import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { CallLog } from '../../components/speedToLead/CallLog';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { listAccounts } from '../accounts';
import { useCurrentUser } from '../auth';
import { DirectTestLeadPanel, PoolsEditor } from '../speedToLead';
import { confirmCallOutcome, deleteRecording, fetchLeadHistory, fetchRecordingUrl } from '../speedToLead/api';
import { fetchAllCalls } from './api';

// THE KYCKCONNECT PAGES THAT ARE NOT AN ACCOUNT TAB (redesign Phase 1): Calls (every club), Numbers and pools, and the Test lab.
// Each reuses an existing component; only the host and the club picker are new.

function useClubOptions() {
  const accounts = useQuery({ queryKey: ['accounts', 'all'], queryFn: listAccounts, staleTime: 5 * 60_000 });
  const clubs = useMemo(
    () => (accounts.data ?? []).filter((a) => a.accountType === 'location' || a.accountType === 'dealership').sort((a, b) => a.name.localeCompare(b.name)),
    [accounts.data],
  );
  return { clubs, options: clubs.map((a) => ({ value: a.id, label: a.name })), names: new Map(clubs.map((a) => [a.id, a.name])) };
}

/** Every club's calls, newest first, with an optional club filter. The all-clubs read is admin only (the server decides). */
export function AllCallsPage() {
  const me = useCurrentUser();
  const isAdmin = me.role === 'admin';
  const { options, names } = useClubOptions();
  const [clubId, setClubId] = useState('');
  const list = useInfiniteQuery({
    queryKey: ['kyckconnect', 'calls', clubId || 'all'],
    queryFn: ({ pageParam }) => fetchAllCalls(clubId || null, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    // Without a club the read is admin only: a non-admin sees the prompt below rather than a refused request.
    enabled: isAdmin || clubId !== '',
  });
  const rows = list.data?.pages.flatMap((p) => p.calls) ?? [];
  const clubOf = new Map(rows.map((r) => [`${r.row.kind}-${r.row.id}`, r.accountId]));

  return (
    <div className="space-y-4">
      <PageHeader title="Calls" description="Every Speed to Lead and forwarded call, newest first. Open a call for its recording, transcript, summary and the lead's history." />
      <div className="max-w-sm">
        <Combobox value={clubId} onValueChange={setClubId} options={[{ value: '', label: 'All clubs' }, ...options]} placeholder="All clubs" searchPlaceholder="Search clubs" aria-label="Club" />
      </div>
      {!isAdmin && clubId === '' && <p className="text-sm text-slate-600">Choose a club to see its calls. The all-clubs view is for admins.</p>}
      {list.isLoading && <Spinner />}
      {list.error && <p className="text-sm text-red-600">{list.error instanceof ApiError || list.error instanceof Error ? list.error.message : 'Could not load the calls.'}</p>}
      {list.data && (
        <>
          <CallLog
            calls={rows.map((r) => r.row)}
            clubLabel={(row) => {
              const id = clubOf.get(`${row.kind}-${row.id}`);
              return id ? (names.get(id) ?? null) : 'Not routed to a club';
            }}
            loadPlayUrl={async (kind, id) => (await fetchRecordingUrl(kind, id)).url}
            loadHistory={(kind, id, cursor) => fetchLeadHistory({ call: `${kind}:${id}` }, cursor)}
            onConfirmOutcome={async (kind, id, outcome) => {
              await confirmCallOutcome(kind, id, outcome);
            }}
            onDelete={isAdmin ? async (kind, id) => { await deleteRecording(kind, id); } : undefined}
          />
          {list.hasNextPage && (
            <Button variant="outline" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** The number pool editor (moved unchanged from the old Settings -> Speed to Lead page). Anyone can read; only an admin writes. */
export function NumbersPage() {
  const me = useCurrentUser();
  return (
    <div className="space-y-4">
      <PageHeader title="Numbers and pools" description="Which Vonage numbers serve which clubs. A pool is up to 20 clubs of one brand sharing numbers; a dedicated number is a pool of one." />
      <PoolsEditor isAdmin={me.role === 'admin'} />
    </div>
  );
}

/** The direct test lead panel with a club picker. Admin only: a test lead texts and rings a real phone. */
export function TestLabPage() {
  const me = useCurrentUser();
  const { options } = useClubOptions();
  const [clubId, setClubId] = useState('');
  if (me.role !== 'admin') {
    return (
      <div>
        <PageHeader title="Test lab" description="Send a made-up lead through a club's Speed to Lead and AI SMS." />
        <p className="text-sm text-slate-600">Only a Kyck admin can send a test lead, because it texts and rings a real phone.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PageHeader title="Test lab" description="Send a made-up lead through a club's Speed to Lead and AI SMS, exactly as a real one would go. It is not sent to the club's CRM and is left out of every report." />
      <div className="max-w-sm">
        <Combobox value={clubId} onValueChange={setClubId} options={options} placeholder="Choose a club" searchPlaceholder="Search clubs" aria-label="Club" />
      </div>
      {clubId === '' ? <p className="text-sm text-slate-500">Pick a club to send a test lead for.</p> : <DirectTestLeadPanel key={clubId} clubId={clubId} />}
    </div>
  );
}
